import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, TrendingUp, TrendingDown, Eye, Heart, Star, DollarSign, BarChart3, Activity, Calendar, ArrowUpRight, ArrowDownRight, ShoppingCart, Package, Coins, Users, Plus, ArrowRight, Sparkles, Send, Download, Copy, CheckCircle, List, ChevronUp, ChevronDown, ChevronRight, ArrowLeftRight } from 'lucide-react';
import { useWallet } from '../contexts/WalletContext';
import WalletRequired from '../components/WalletRequired';
import { supabase } from '../lib/supabase';
import { getPiecesByHolder, getUserListings, getCapsuleForPiece } from '../utils/supabaseDatabase';
import { getWalletOffers } from '../utils/offersContract';
import { filterActivitiesByTime } from '../utils/activityUtils';
import ModelViewer from '../components/ModelViewer.jsx';
import PieceActivityAndHolders from '../components/PieceActivityAndHolders';
import { getCategoryDisplayName } from '../utils/pieceUtils';
import { normalizePieces } from '../utils/pieceNormalizer.js';
import { useSolPrice } from '../contexts/SolPriceContext';
import { useFavorites } from '../hooks/useFavorites';
import VerifiedBadge from '../components/VerifiedBadge';
import { useWalletAction } from '../hooks/useWalletAction';
import { purchasePiece } from '../utils/editionPurchase';
import { 
  getActiveListing, 
  canListPiece, 
  canPurchaseListing
} from '../utils/resaleMarketplace';
import RequestTradeModal from '../components/RequestTradeModal';

const Portfolio = () => {
  const navigate = useNavigate();
  const { isConnected, balance, publicKey, usdPrice, wallet } = useWallet();
  const { solPrice } = useSolPrice();
  const { toggleFavorite, isFavorited } = useFavorites();
  const { withWalletCheck } = useWalletAction();
  const [selectedTimeframe, setSelectedTimeframe] = useState('7d');
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [portfolioStats, setPortfolioStats] = useState({
    totalValue: '0.00 SOL',
    change24h: '+0.00%',
    change7d: '+0.00%',
    change30d: '+0.00%',
    totalItems: 0,
    activeListings: 0,
    pendingOffers: 0,
    totalSpent: '0.00 SOL',
    totalEarned: '0.00 SOL',
    piecesCreated: 0,
    capsulesCreated: 0,
    outfitsCreated: 0,
    piecesOwned: 0,
    walletBalance: '0.00 SOL'
  });

  // Modal state for piece details
  const [pieceDetailModal, setPieceDetailModal] = useState({ isOpen: false, item: null });
  const [modalViewMode, setModalViewMode] = useState('3d'); // '3d' or 'thumbnail'
  const [activityPanelOpen, setActivityPanelOpen] = useState(false);
  const [threeDStatus, setThreeDStatus] = useState('loading'); // 'loading', 'loaded', 'error'
  const [threeDError, setThreeDError] = useState(null);
  const [pieceCapsuleInfo, setPieceCapsuleInfo] = useState(null);
  const [groupedPieceIds, setGroupedPieceIds] = useState([]);
  const [allEditions, setAllEditions] = useState([]);
  const [selectedEditionIndex, setSelectedEditionIndex] = useState(0);
  const [showEditionSelector, setShowEditionSelector] = useState(false);
  
  // Trading/buying functionality state
  const [requestTradeModal, setRequestTradeModal] = useState({ isOpen: false, item: null });
  const [offerModal, setOfferModal] = useState({ 
    isOpen: false, 
    item: null, 
    offerAmount: '', 
    expirationDays: 7, 
    expirationHours: 0 
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: 'success', message: '' });
  const [activeListing, setActiveListing] = useState(null);
  const [listingLoading, setListingLoading] = useState(false);

  // Calculate portfolio statistics from real database data
  useEffect(() => {
    const calculateStats = async () => {
      if (!isConnected || !publicKey) {
        console.log('Wallet not connected, skipping stats calculation');
        setIsLoadingStats(false);
        return;
      }

      try {
        setIsLoadingStats(true);
        const walletAddress = publicKey.toString();
        console.log('📊 Calculating portfolio stats for wallet:', walletAddress, 'with timeframe:', selectedTimeframe);

        // Get ALL pieces created by this wallet (including drafts and listed)
        let totalPiecesCreated = 0;
        let piecesCreated = [];
        
        try {
          const { data: creator, error: creatorError } = await supabase
            .from('creators')
            .select('id')
            .eq('wallet', walletAddress)
            .maybeSingle();

          if (!creatorError && creator) {
            const { data: allCreatedPieces, count, error: piecesError } = await supabase
              .from('pieces')
              .select('*', { count: 'exact' })
              .eq('creator_id', creator.id)
              .order('created_at', { ascending: false })
              .limit(100);
            
            if (!piecesError) {
              piecesCreated = allCreatedPieces || [];
              totalPiecesCreated = count || 0;
            }
          }
        } catch (err) {
          console.warn('Error fetching created pieces:', err);
        }

        // Get ALL pieces owned by this wallet (holder_wallet - including any status)
        let piecesOwned = [];
        let totalPiecesOwned = 0;
        
        try {
          const { data: allOwnedPieces, count: ownedCount, error: ownedError } = await supabase
            .from('pieces')
            .select('*', { count: 'exact' })
            .eq('holder_wallet', walletAddress)
            .order('created_at', { ascending: false })
            .limit(100);
          
          if (!ownedError) {
            piecesOwned = allOwnedPieces || [];
            totalPiecesOwned = ownedCount || 0;
          }
        } catch (err) {
          console.warn('Error fetching owned pieces:', err);
        }

        // Get active listings for this wallet
        let activeListings = 0;
        try {
          const result = await getUserListings(walletAddress, 'listed');
          activeListings = result?.listings?.length || 0;
          console.log('✅ Active listings count:', activeListings);
        } catch (err) {
          console.warn('Error fetching listings:', err);
        }

        // Get offers for this wallet
        let pendingOffers = 0;
        try {
          const offersResult = await getWalletOffers(walletAddress, 'all', 'active');
          const activeOffers = offersResult.offers || [];
          pendingOffers = activeOffers.length;
        } catch (err) {
          console.warn('Error fetching offers:', err);
        }

        // Get wallet activity from nft_activity table
        const { data: activityData, error: activityError } = await supabase
          .rpc('get_wallet_activity', { 
            wallet_addr: walletAddress,
            limit_count: 500 // Fetch more to have enough after time filtering
          });

        let totalSpent = 0;
        let totalEarned = 0;
        let collectionValue = 0;

        if (!activityError && activityData) {
          // Convert timeframe to milliseconds for filtering
          const now = Date.now();
          const timeRanges = {
            '24h': 24 * 60 * 60 * 1000,
            '7d': 7 * 24 * 60 * 60 * 1000,
            '30d': 30 * 24 * 60 * 60 * 1000,
            '90d': 90 * 24 * 60 * 60 * 1000,
            '1y': 365 * 24 * 60 * 60 * 1000
          };
          const timeLimit = timeRanges[selectedTimeframe];

          let totalActivities = 0;
          let filteredCount = 0;
          let spentCount = 0;
          let earnedCount = 0;

          // Calculate spent (purchases) and earned (sales) - filtered by timeframe
          activityData.forEach(activity => {
            totalActivities++;
            
            // Convert event_timestamp (seconds) to milliseconds
            const activityTime = activity.event_timestamp ? activity.event_timestamp * 1000 : Date.now();
            const ageInMs = now - activityTime;
            const ageInDays = ageInMs / (24 * 60 * 60 * 1000);
            
            // Skip if activity is outside selected timeframe
            if (timeLimit && ageInMs > timeLimit) {
              return;
            }

            filteredCount++;
            const amount = parseFloat(activity.sol_amount || 0);
            
            // If this wallet is the buyer (to_wallet)
            if (activity.event_type === 'sale' && activity.to_wallet === walletAddress) {
              totalSpent += amount;
              spentCount++;
            }
            
            // If this wallet is the seller (from_wallet)
            if (activity.event_type === 'sale' && activity.from_wallet === walletAddress) {
              totalEarned += amount;
              earnedCount++;
            }
          });

          console.log(`💰 Stats for ${selectedTimeframe}:`, {
            totalActivitiesScanned: totalActivities,
            activitiesInTimeframe: filteredCount,
            purchaseEvents: spentCount,
            saleEvents: earnedCount,
            totalSpent: totalSpent.toFixed(2),
            totalEarned: totalEarned.toFixed(2),
            oldestActivityAge: totalActivities > 0 ? 
              `${((now - (activityData[activityData.length - 1].event_timestamp * 1000)) / (24 * 60 * 60 * 1000)).toFixed(1)} days` : 
              'N/A'
          });
        }

        // Calculate collection value from owned pieces
        piecesOwned.forEach(piece => {
          const price = parseFloat(piece.price || piece.listing_price || 0);
          collectionValue += price;
        });

        // Get capsules created by this wallet (using creator_id)
        let capsulesCreated = 0;
        try {
          // First get the creator record
          const { data: creator, error: creatorError } = await supabase
            .from('creators')
            .select('id')
            .eq('wallet', walletAddress)
            .maybeSingle();

          if (!creatorError && creator) {
            // Then count capsules by creator_id
            const { count: capsulesCount, error: capsulesError } = await supabase
              .from('capsules')
              .select('*', { count: 'exact', head: true })
              .eq('creator_id', creator.id);
            
            if (!capsulesError) {
              capsulesCreated = capsulesCount || 0;
              console.log('✅ Found', capsulesCreated, 'capsules created by this wallet');
            }
          }
        } catch (err) {
          console.warn('Error fetching capsules:', err);
        }

        // Get outfits created by this wallet (from localStorage)
        let outfitsCreated = 0;
        try {
          const savedOutfits = localStorage.getItem('outfits');
          if (savedOutfits) {
            const outfits = JSON.parse(savedOutfits);
            // Filter outfits created by this wallet
            const userOutfits = outfits.filter(outfit => 
              outfit.creatorWallet === walletAddress || outfit.creator_wallet === walletAddress
            );
            outfitsCreated = userOutfits.length;
            console.log('✅ Found', outfitsCreated, 'outfits in localStorage for this wallet');
          }
        } catch (err) {
          console.warn('Error fetching outfits from localStorage:', err);
        }

        // Calculate total portfolio value (only collection value, excluding wallet balance)
        const totalPortfolioValue = collectionValue;
        const totalItems = totalPiecesCreated + capsulesCreated;

        console.log('📊 Portfolio stats calculated:', {
          totalPiecesCreated,
          totalPiecesOwned,
          capsulesCreated,
          outfitsCreated,
          activeListings,
          pendingOffers,
          totalSpent,
          totalEarned,
          collectionValue,
          totalPortfolioValue
        });

        // Update portfolio stats
        setPortfolioStats(prev => ({
          ...prev,
          totalValue: `${totalPortfolioValue.toFixed(2)} SOL`,
          walletBalance: `${balance.toFixed(2)} SOL`,
          totalSpent: `${totalSpent.toFixed(2)} SOL`,
          totalEarned: `${totalEarned.toFixed(2)} SOL`,
          piecesCreated: totalPiecesCreated,
          capsulesCreated: capsulesCreated,
          outfitsCreated: outfitsCreated,
          piecesOwned: totalPiecesOwned,
          activeListings: activeListings,
          pendingOffers: pendingOffers,
          totalItems: totalItems
        }));

        setIsLoadingStats(false);

      } catch (error) {
        console.error('❌ Error calculating portfolio stats:', error);
        setIsLoadingStats(false);
      }
    };

    calculateStats();
  }, [balance, isConnected, publicKey, selectedTimeframe]);

  // Generate recent activity from real blockchain data - ONLY for connected wallet
  const [recentActivity, setRecentActivity] = useState([]);
  const [allActivitiesUnfiltered, setAllActivitiesUnfiltered] = useState([]);
  const [copiedAddress, setCopiedAddress] = useState(null);

  useEffect(() => {
    const loadRecentActivity = async () => {
      if (!isConnected || !publicKey) {
        setRecentActivity([]);
        setAllActivitiesUnfiltered([]);
        return;
      }

      try {
        const walletAddress = publicKey.toString();
        console.log('📊 Loading recent activity for wallet:', walletAddress);

        // Get wallet activity from nft_activity table - fetch more to have enough after filtering
        const { data: activityData, error: activityError } = await supabase
          .rpc('get_wallet_activity', { 
            wallet_addr: walletAddress,
            limit_count: 500 // Fetch more to ensure all activities are included
          });

        if (activityError) {
          console.error('❌ Error fetching wallet activity:', activityError);
          console.error('Error details:', {
            message: activityError.message,
            details: activityError.details,
            hint: activityError.hint,
            code: activityError.code
          });
          setRecentActivity([]);
          return;
        }

        console.log('📦 Raw activity data received:', activityData);
        console.log('📊 Total activities fetched from database:', activityData?.length || 0);

        if (!activityData || activityData.length === 0) {
          console.log('ℹ️ No activity found for this wallet');
          setRecentActivity([]);
          return;
        }

        // Get piece details for the activities (including price info and edition data)
        const mintAddresses = activityData.map(a => a.mint_address).filter(Boolean);
        let piecesMap = {};
        let masterPiecesMap = {};

        if (mintAddresses.length > 0) {
          const { data: piecesData } = await supabase
            .from('pieces')
            .select('id, mint_address, name, thumbnail_url, category, price, listing_price, quantity, master_id, edition_number, is_master, total_quantity, creator_id')
            .in('mint_address', mintAddresses);

          if (piecesData) {
            // Create pieces map
            piecesMap = piecesData.reduce((acc, piece) => {
              acc[piece.mint_address] = piece;
              return acc;
            }, {});

            // Get all unique master IDs
            const masterIds = [...new Set(piecesData.filter(p => p.master_id).map(p => p.master_id))];
            
            // Fetch master pieces info
            if (masterIds.length > 0) {
              const { data: masterData } = await supabase
                .from('pieces')
                .select('id, name, thumbnail_url, category, price, listing_price, total_quantity')
                .in('id', masterIds);
              
              if (masterData) {
                masterPiecesMap = masterData.reduce((acc, master) => {
                  acc[master.id] = master;
                  return acc;
                }, {});
              }
            }
          }
        }

        // Consolidate activities by piece name, event type, and wallets (matching Activity page logic)
        // This groups activities that happened within 60 seconds with the same piece, event type, and wallets
        const consolidatedActivities = [];
        const processedIndices = new Set();

        // Sort activities by timestamp first (most recent first)
        const sortedActivities = [...activityData].sort((a, b) => b.event_timestamp - a.event_timestamp);

        // Debug: Log all activities for "test 50" to see timestamps and detect grouping issues
        const test50Activities = sortedActivities.filter(a => {
          const piece = piecesMap[a.mint_address];
          const masterPiece = piece && piece.master_id ? masterPiecesMap[piece.master_id] : null;
          const displayPiece = masterPiece || piece;
          const pieceName = displayPiece?.name || piece?.name;
          return pieceName?.toLowerCase().includes('test 50'.toLowerCase());
        });
        
        console.log(`🔍 [PORTFOLIO] Searching for "test 50" activities in ${sortedActivities.length} total activities...`);
        
        if (test50Activities.length > 0) {
          console.log(`🔍 [PORTFOLIO] Found ${test50Activities.length} "test 50" activities:`);
          test50Activities.forEach((a, idx) => {
            const piece = piecesMap[a.mint_address];
            const masterPiece = piece && piece.master_id ? masterPiecesMap[piece.master_id] : null;
            const displayPiece = masterPiece || piece;
            console.log(`  ${idx + 1}. Name: "${displayPiece?.name || piece?.name}", Type: ${a.event_type}, From: ${a.from_wallet?.slice(0, 8)}, To: ${a.to_wallet?.slice(0, 8)}, Timestamp: ${a.event_timestamp}, Time diff: ${idx > 0 ? Math.abs(test50Activities[idx].event_timestamp - test50Activities[idx-1].event_timestamp) : 0}s`);
          });
        } else {
          console.log(`⚠️ [PORTFOLIO] No "test 50" activities found in ${sortedActivities.length} activities`);
        }

        sortedActivities.forEach((activity, index) => {
          // Skip if already processed
          if (processedIndices.has(index)) return;

          const piece = piecesMap[activity.mint_address];
          const masterPiece = piece && piece.master_id ? masterPiecesMap[piece.master_id] : null;
          const displayPiece = masterPiece || piece;
          const pieceName = displayPiece?.name || piece?.name || `Piece ${activity.mint_address?.slice(0, 8)}...`;
          
          // Determine activity type
          const isIncoming = activity.direction === 'incoming';
          const isOutgoing = activity.direction === 'outgoing';
          let type = activity.event_type;
          let eventLabel = '';
          
          if (activity.event_type === 'mint') {
            eventLabel = 'Minted';
            type = 'PIECE_MINTED';
          } else if (activity.event_type === 'list') {
            eventLabel = 'Listed';
            type = 'PIECE_LISTED';
          } else if (activity.event_type === 'sale' && isOutgoing) {
            eventLabel = 'Sold';
            type = 'PIECE_SOLD';
          } else if (activity.event_type === 'sale' && isIncoming) {
            eventLabel = 'Bought';
            type = 'PIECE_BOUGHT';
          } else if (activity.event_type === 'transfer' && isOutgoing) {
            eventLabel = 'Sent';
            type = 'PIECE_SENT';
          } else if (activity.event_type === 'transfer' && isIncoming) {
            eventLabel = 'Received';
            type = 'PIECE_RECEIVED';
          } else {
            eventLabel = activity.event_type;
            type = activity.event_type.toUpperCase();
          }

          // Check if this should be consolidated (mint, list, sale events)
          const isConsolidatableType = type === 'PIECE_MINTED' || type === 'PIECE_LISTED' || type === 'PIECE_SOLD' || type === 'PIECE_BOUGHT';
          
          if (isConsolidatableType && pieceName) {
            const group = {
              activities: [activity],
              pieces: [piece],
              count: 1,
              mintAddresses: [activity.mint_address],
              earliestTimestamp: activity.event_timestamp,
              latestTimestamp: activity.event_timestamp
            };
            
            processedIndices.add(index);

            // Look for similar activities within 60 seconds
            let foundNewMatch = true;
            while (foundNewMatch) {
              foundNewMatch = false;
              
              for (let j = index + 1; j < sortedActivities.length; j++) {
                if (processedIndices.has(j)) continue;
                
                const otherActivity = sortedActivities[j];
                const otherPiece = piecesMap[otherActivity.mint_address];
                const otherMasterPiece = otherPiece && otherPiece.master_id ? masterPiecesMap[otherPiece.master_id] : null;
                const otherDisplayPiece = otherMasterPiece || otherPiece;
                const otherPieceName = otherDisplayPiece?.name || otherPiece?.name || `Piece ${otherActivity.mint_address?.slice(0, 8)}...`;
                
                // Determine other activity type
                const otherIsIncoming = otherActivity.direction === 'incoming';
                const otherIsOutgoing = otherActivity.direction === 'outgoing';
                let otherType = otherActivity.event_type;
                
                if (otherActivity.event_type === 'mint') {
                  otherType = 'PIECE_MINTED';
                } else if (otherActivity.event_type === 'list') {
                  otherType = 'PIECE_LISTED';
                } else if (otherActivity.event_type === 'sale' && otherIsOutgoing) {
                  otherType = 'PIECE_SOLD';
                } else if (otherActivity.event_type === 'sale' && otherIsIncoming) {
                  otherType = 'PIECE_BOUGHT';
                } else if (otherActivity.event_type === 'transfer' && otherIsOutgoing) {
                  otherType = 'PIECE_SENT';
                } else if (otherActivity.event_type === 'transfer' && otherIsIncoming) {
                  otherType = 'PIECE_RECEIVED';
                } else {
                  otherType = otherActivity.event_type.toUpperCase();
                }
                
                // Check time difference against the entire group's time range
                const timeDiffFromEarliest = Math.abs(group.earliestTimestamp - otherActivity.event_timestamp);
                const timeDiffFromLatest = Math.abs(group.latestTimestamp - otherActivity.event_timestamp);
                
                // Stop looking if we've gone too far back in time (60 seconds)
                if (Math.min(timeDiffFromEarliest, timeDiffFromLatest) > 60) {
                  if (otherActivity.event_timestamp < group.earliestTimestamp - 60) {
                    break;
                  }
                  continue;
                }
                
                // Check if it's the same type of activity (same event type, piece name, and wallets)
                if (
                  otherType === type &&
                  otherPieceName === pieceName &&
                  otherActivity.from_wallet === activity.from_wallet &&
                  otherActivity.to_wallet === activity.to_wallet
                ) {
                  group.count++;
                  group.activities.push(otherActivity);
                  group.pieces.push(otherPiece);
                  group.mintAddresses.push(otherActivity.mint_address);
                  processedIndices.add(j);
                  foundNewMatch = true;
                  
                  // Update the group's time range
                  if (otherActivity.event_timestamp < group.earliestTimestamp) {
                    group.earliestTimestamp = otherActivity.event_timestamp;
                  }
                  if (otherActivity.event_timestamp > group.latestTimestamp) {
                    group.latestTimestamp = otherActivity.event_timestamp;
                  }
                }
              }
            }

            // Add consolidated group
            const firstActivity = group.activities[0];
            const timestamp = group.earliestTimestamp * 1000; // Convert to milliseconds

            // Try to get price from multiple sources
            let solAmount = parseFloat(firstActivity.sol_amount || 0);
            
            // If no price in activity and this is a sale/purchase, try to get from piece
            if (solAmount === 0 && (type === 'PIECE_SOLD' || type === 'PIECE_BOUGHT')) {
              solAmount = parseFloat((displayPiece?.price || displayPiece?.listing_price || 0));
            }

            consolidatedActivities.push({
              id: `activity-group-${masterPiece?.id || piece?.id || firstActivity.id}`,
              type: type,
              eventLabel: eventLabel,
              pieceName: pieceName,
              image: displayPiece?.thumbnail_url || null,
              collection: displayPiece?.category || 'Digital Fashion',
              price: solAmount > 0 ? `${solAmount.toFixed(2)} SOL` : '-',
              usdPrice: solAmount > 0 ? `$${(solAmount * (usdPrice || 195.84)).toFixed(2)} USD` : null,
              quantity: group.count.toString(),
              from: firstActivity.from_wallet || '-',
              to: firstActivity.to_wallet || '-',
              timeAgo: firstActivity.time_ago || 'Recently',
              mintAddress: firstActivity.mint_address,
              signature: firstActivity.signature,
              direction: firstActivity.direction,
              timestamp: timestamp
            });
          } else {
            // Not a consolidatable event, keep as-is
            if (!processedIndices.has(index)) {
              const timestamp = activity.event_timestamp ? activity.event_timestamp * 1000 : Date.now();

              // Try to get price from multiple sources
              let solAmount = parseFloat(activity.sol_amount || 0);
              
              // If no price in activity and this is a sale/purchase, try to get from piece
              if (solAmount === 0 && (type === 'PIECE_SOLD' || type === 'PIECE_BOUGHT')) {
                solAmount = parseFloat((displayPiece?.price || displayPiece?.listing_price || 0));
              }

              consolidatedActivities.push({
                id: `activity-single-${activity.id}`,
                type: type,
                eventLabel: eventLabel,
                pieceName: pieceName,
                image: displayPiece?.thumbnail_url || null,
                collection: displayPiece?.category || 'Digital Fashion',
                price: solAmount > 0 ? `${solAmount.toFixed(2)} SOL` : '-',
                usdPrice: solAmount > 0 ? `$${(solAmount * (usdPrice || 195.84)).toFixed(2)} USD` : null,
                quantity: '1',
                from: activity.from_wallet || '-',
                to: activity.to_wallet || '-',
                timeAgo: activity.time_ago || 'Recently',
                mintAddress: activity.mint_address,
                signature: activity.signature,
                direction: activity.direction,
                timestamp: timestamp
              });
              processedIndices.add(index);
            }
          }
        });

        // Sort again by timestamp (most recent first)
        consolidatedActivities.sort((a, b) => b.timestamp - a.timestamp);
        
        console.log(`📊 [PORTFOLIO] Consolidated ${activityData.length} activities into ${consolidatedActivities.length} (grouped ${activityData.length - consolidatedActivities.length} activities)`);
        
        // Log detailed grouping info for activities with quantity > 1
        consolidatedActivities.forEach(activity => {
          if (parseInt(activity.quantity) > 1) {
            console.log(`📦 [PORTFOLIO] Grouped Activity:`, {
              pieceName: activity.pieceName,
              type: activity.type,
              quantity: activity.quantity,
              from: activity.from?.slice(0, 8),
              to: activity.to?.slice(0, 8),
              timeAgo: activity.timeAgo
            });
          }
        });

        const formattedActivities = consolidatedActivities;

        console.log('✅ Loaded', formattedActivities.length, 'activities (unfiltered)');
        setAllActivitiesUnfiltered(formattedActivities);

      } catch (error) {
        console.error('❌ Error loading recent activity:', error);
        setRecentActivity([]);
        setAllActivitiesUnfiltered([]);
      }
    };

    loadRecentActivity();
  }, [isConnected, publicKey, usdPrice]);

  // Filter activities whenever selectedTimeframe changes
  useEffect(() => {
    if (allActivitiesUnfiltered.length === 0) {
      setRecentActivity([]);
      return;
    }

    console.log(`🔍 Filtering ${allActivitiesUnfiltered.length} activities by timeframe: ${selectedTimeframe}`);
    
    // Check age of activities for debugging
    const now = Date.now();
    const ages = allActivitiesUnfiltered.map(a => {
      const age = (now - a.timestamp) / (24 * 60 * 60 * 1000);
      return age.toFixed(1);
    });
    console.log(`📅 Activity ages (days):`, ages.slice(0, 5), '...');
    
    const filtered = filterActivitiesByTime(allActivitiesUnfiltered, selectedTimeframe);
    console.log(`✅ Filtered to ${filtered.length} activities (from ${allActivitiesUnfiltered.length})`);
    
    // Show all events
    setRecentActivity(filtered);
  }, [allActivitiesUnfiltered, selectedTimeframe]);

  const timeframes = ["24h", "7d", "30d", "90d", "1y"];

  // Activity event types (matching Activity page)
  const eventTypes = {
    'PIECE_MINTED': { icon: Package, label: 'Minted' },
    'PIECE_LISTED': { icon: List, label: 'Listed' },
    'PIECE_SOLD': { icon: ShoppingCart, label: 'Sold' },
    'PIECE_BOUGHT': { icon: Download, label: 'Bought' },
    'PIECE_SENT': { icon: Send, label: 'Sent' },
    'PIECE_RECEIVED': { icon: Download, label: 'Received' },
    'OFFER_MADE': { icon: DollarSign, label: 'Offer Made' },
    'OFFER_ACCEPTED': { icon: CheckCircle, label: 'Offer Accepted' }
  };

  const getActivityIcon = (type) => {
    const eventType = eventTypes[type];
    return eventType ? eventType.icon : Activity;
  };

  // Copy address to clipboard
  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedAddress(type);
      setTimeout(() => setCopiedAddress(null), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  // Handle opening piece detail modal
  const openPieceDetailModal = async (activity) => {
    if (!activity) {
      console.error('❌ No activity provided to openPieceDetailModal');
      return;
    }

    console.log('🔍 [PORTFOLIO] Opening modal for activity:', activity);

    try {
      // Get the piece ID from the mint address
      let pieceData = null;
      
      if (activity.mintAddress) {
        const { data, error } = await supabase
          .from('pieces')
          .select(`
            *,
            social_links,
            creators (
              id,
              wallet,
              handle,
              is_verified,
              verification_type,
              profile_picture_url,
              banner_url,
              bio,
              social_links,
              total_pieces_created
            )
          `)
          .eq('mint_address', activity.mintAddress)
          .single();

        if (!error && data) {
          pieceData = data;
        }
      }

      if (!pieceData) {
        // Fallback to basic data from activity
        pieceData = {
          id: activity.id,
          name: activity.pieceName,
          thumbnail_url: activity.image,
          category: activity.collection,
          price: parseFloat(activity.price?.replace(' SOL', '') || '0'),
          quantity: activity.quantity,
          description: 'A premium digital fashion piece with modern styling and high-quality design.'
        };
      }

      // Normalize the piece data
      const normalizedItem = pieceData ? normalizePieces([pieceData])[0] : null;

      // Fetch capsule info if piece belongs to a capsule
      try {
        const capsuleInfo = await getCapsuleForPiece(normalizedItem?.id);
        setPieceCapsuleInfo(capsuleInfo);
      } catch (capsuleError) {
        console.error('❌ Error fetching capsule info:', capsuleError);
        setPieceCapsuleInfo(null);
      }

      // Fetch all editions (pieces with same name) for edition selector
      try {
        if (normalizedItem?.name || pieceData?.name) {
          const pieceName = normalizedItem?.name || pieceData?.name;
          console.log('🔍 [PORTFOLIO] Fetching all editions for:', pieceName);
          
          const { data: allPieces, error: editionsError } = await supabase
            .from('pieces')
            .select('*')
            .eq('name', pieceName)
            .order('created_at', { ascending: true });

          if (!editionsError && allPieces && allPieces.length > 0) {
            console.log(`✅ [PORTFOLIO] Found ${allPieces.length} editions for "${pieceName}"`);
            setAllEditions(allPieces);
            setGroupedPieceIds(allPieces.map(p => p.id));
            
            // Set selected edition to the current piece
            const currentIndex = allPieces.findIndex(p => p.id === normalizedItem?.id || p.mint_address === activity.mintAddress);
            if (currentIndex !== -1) {
              setSelectedEditionIndex(currentIndex);
            } else {
              setSelectedEditionIndex(0);
            }
          } else {
            console.log('ℹ️ [PORTFOLIO] Only one edition found');
            setAllEditions([normalizedItem || pieceData]);
            setGroupedPieceIds([normalizedItem?.id || pieceData?.id]);
            setSelectedEditionIndex(0);
          }
        }
      } catch (editionsError) {
        console.error('❌ Error fetching editions:', editionsError);
        setAllEditions([normalizedItem || pieceData]);
        setGroupedPieceIds([normalizedItem?.id || pieceData?.id]);
        setSelectedEditionIndex(0);
      }

      // Check if piece has 3D model and set initial view mode
      const has3DModel = normalizedItem?.previewUrl && (normalizedItem.previewUrl.includes('.glb') || normalizedItem.previewUrl.includes('.gltf'));
      
      if (has3DModel) {
        setModalViewMode('3d');
        setThreeDStatus('loading');
        setThreeDError(null);
      } else {
        setModalViewMode('thumbnail');
        setThreeDStatus('error');
        setThreeDError('No 3D model available');
      }

      setPieceDetailModal({ isOpen: true, item: normalizedItem || pieceData });
      
    } catch (error) {
      console.error('❌ Exception fetching piece data:', error);
      
      // Fallback to basic data
      const fallbackData = {
        id: activity.id,
        name: activity.pieceName,
        thumbnailUrl: activity.image,
        description: 'A premium digital fashion piece with modern styling and high-quality design.',
        price: parseFloat(activity.price?.replace(' SOL', '') || '0'),
        quantity: activity.quantity
      };
      setPieceDetailModal({ isOpen: true, item: fallbackData });
      setModalViewMode('thumbnail');
    }
  };

  const closePieceDetailModal = () => {
    setPieceDetailModal({ isOpen: false, item: null });
    setModalViewMode('3d');
    setThreeDStatus('loading');
    setThreeDError(null);
    setActivityPanelOpen(false);
    setPieceCapsuleInfo(null);
    // Reset edition state
    setGroupedPieceIds([]);
    setAllEditions([]);
    setSelectedEditionIndex(0);
    setShowEditionSelector(false);
  };

  // Handle clicking on creator/account
  const handleAccountClick = (piece) => {
    const creatorWallet = piece?.creator?.wallet || piece?.creator_wallet || piece?.originalCreator?.wallet;
    if (creatorWallet) {
      navigate(`/creator/${creatorWallet}`);
      closePieceDetailModal();
    }
  };

  // Handle Buy Now
  const handleBuyNow = (piece) => {
    console.log('🛒 [PORTFOLIO] Buy now clicked for piece:', piece);
    // Implement buy logic here - this would typically open a purchase modal
    // For now, just log the action
  };

  // Handle Purchase Listing (for secondary market)
  const handlePurchaseListing = (piece) => {
    console.log('🛒 [PORTFOLIO] Purchase listing clicked for piece:', piece);
    // Implement purchase listing logic here
  };

  // Handle Make Offer
  const handleMakeOffer = (piece) => {
    console.log('💰 [PORTFOLIO] Make offer clicked for piece:', piece);
    setOfferModal({ 
      isOpen: true, 
      item: piece,
      offerAmount: '', 
      expirationDays: 7, 
      expirationHours: 0 
    });
  };

  // Handle Request Trade
  const handleRequestTrade = (piece) => {
    console.log('🔄 [PORTFOLIO] Request trade clicked for piece:', piece);
    if (!isConnected) {
      alert('Please connect your wallet to request a trade');
      return;
    }
    setRequestTradeModal({ isOpen: true, item: piece });
  };

  // Handle View Capsule
  const handleViewCapsule = () => {
    if (pieceCapsuleInfo) {
      navigate(`/capsule/${pieceCapsuleInfo.id}`);
      closePieceDetailModal();
    }
  };

  return (
    <WalletRequired 
      message="You need to connect your wallet to view your portfolio and manage your pieces."
      action="access your portfolio"
    >
      <div className="min-h-screen bg-black">
        {/* Header Section */}
        <div className="max-w-full mx-auto px-4 pt-8 pb-4">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Portfolio</h1>
              <p className="text-gray-400">Your digital fashion portfolio overview</p>
            </div>
            
            {/* Timeframe Selector */}
            <div className="flex items-center gap-1 bg-[#0a0a0a] p-1 rounded-xl border border-[#2a2a2a] w-fit">
              {timeframes.map((timeframe) => (
                <button
                  key={timeframe}
                  onClick={() => setSelectedTimeframe(timeframe)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    selectedTimeframe === timeframe 
                      ? 'bg-[#2a2a2a] text-white shadow-lg' 
                      : 'text-gray-400 hover:text-white hover:bg-[#1a1a1a]'
                  }`}
                >
                  {timeframe}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-full mx-auto px-4 pb-6">
        {/* Portfolio Stats */}
        <div className="space-y-8 mb-8">
          {/* Wallet Balance - Centered Above */}
          <div className="flex justify-center">
            <div 
              className="relative p-8 rounded-3xl border-2 border-slate-400/40 shadow-2xl hover:shadow-slate-400/30 transition-all duration-300 hover:scale-105 w-full max-w-2xl overflow-hidden"
              style={{ 
                background: 'linear-gradient(135deg, #64748b 0%, #94a3b8 15%, #cbd5e1 30%, #f1f5f9 45%, #e2e8f0 60%, #cbd5e1 75%, #94a3b8 90%, #64748b 100%)',
                boxShadow: '0 25px 50px -12px rgba(71, 85, 105, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
              }}
            >
              {/* Animated background gradient */}
              <div className="absolute inset-0 bg-gradient-to-r from-slate-400/20 via-transparent to-slate-600/20 animate-pulse"></div>
              
              <div className="relative z-10">
                <div className="mb-6">
                  <h3 className="text-slate-800 text-sm font-semibold uppercase tracking-wide">Wallet Balance</h3>
                  <p className="text-slate-700 text-xs mt-1">Connected to Solana</p>
                </div>
                
                <div className="space-y-2">
                  <div className="text-4xl font-bold text-slate-900 mb-1">{portfolioStats.walletBalance}</div>
                  <div className="text-slate-800 text-lg font-medium">
                    ≈ ${(parseFloat(portfolioStats.walletBalance) * (usdPrice || 195.84)).toFixed(2)} USD
                  </div>
                </div>
                
                {/* Status indicator */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-400/40">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                    <span className={`text-xs font-medium ${isConnected ? 'text-green-700' : 'text-red-700'}`}>
                      {isConnected ? 'Wallet Connected' : 'Wallet Disconnected'}
                    </span>
                  </div>
                  {publicKey && isConnected && (
                    <span className="text-slate-700 text-xs font-mono bg-slate-200/60 px-2 py-1 rounded">
                      {publicKey.toString().slice(0, 8)}...{publicKey.toString().slice(-6)}
                    </span>
                  )}
                </div>
                
              </div>
            </div>
          </div>

          {/* Other Stats - 2x4 Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Total Value */}
            <div 
              className="p-6 rounded-2xl border border-[#2a2a2a] shadow-xl hover:shadow-2xl transition-all duration-200 hover:scale-105"
              style={{ backgroundColor: '#111111' }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-gray-400 text-sm font-medium">Total Value</h3>
                <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center">
                  <DollarSign size={20} className="text-green-400" />
                </div>
              </div>
              <div className={`text-2xl font-bold text-white mb-2 ${isLoadingStats ? 'animate-pulse' : ''}`}>
                {portfolioStats.totalValue}
              </div>
              <div className="text-gray-400 text-sm">
                ≈ ${(parseFloat(portfolioStats.totalValue) * (usdPrice || 195.84)).toFixed(2)} USD
              </div>
            </div>

          {/* Total Spent */}
          <div 
            className="p-6 rounded-2xl border border-[#2a2a2a] shadow-xl hover:shadow-2xl transition-all duration-200 hover:scale-105"
            style={{ backgroundColor: '#111111' }}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-gray-400 text-sm font-medium">Total Spent</h3>
                <p className="text-gray-500 text-xs mt-1">
                  {selectedTimeframe === '24h' ? 'Last 24 hours' :
                   selectedTimeframe === '7d' ? 'Last 7 days' :
                   selectedTimeframe === '30d' ? 'Last 30 days' :
                   selectedTimeframe === '90d' ? 'Last 90 days' : 'Last year'}
                </p>
              </div>
              <div className="w-10 h-10 bg-red-500/20 rounded-xl flex items-center justify-center">
                <ShoppingCart size={20} className="text-red-400" />
              </div>
            </div>
            <div className={`text-2xl font-bold text-white mb-2 ${isLoadingStats ? 'animate-pulse' : ''}`}>
              {portfolioStats.totalSpent}
            </div>
            <div className="text-gray-400 text-sm">
              ≈ ${(parseFloat(portfolioStats.totalSpent) * (usdPrice || 195.84)).toFixed(2)} USD
            </div>
          </div>

          {/* Total Earned */}
          <div 
            className="p-6 rounded-2xl border border-[#2a2a2a] shadow-xl hover:shadow-2xl transition-all duration-200 hover:scale-105"
            style={{ backgroundColor: '#111111' }}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-gray-400 text-sm font-medium">Total Earned</h3>
                <p className="text-gray-500 text-xs mt-1">
                  {selectedTimeframe === '24h' ? 'Last 24 hours' :
                   selectedTimeframe === '7d' ? 'Last 7 days' :
                   selectedTimeframe === '30d' ? 'Last 30 days' :
                   selectedTimeframe === '90d' ? 'Last 90 days' : 'Last year'}
                </p>
              </div>
              <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center">
                <Coins size={20} className="text-green-400" />
              </div>
            </div>
            <div className={`text-2xl font-bold text-white mb-2 ${isLoadingStats ? 'animate-pulse' : ''}`}>
              {portfolioStats.totalEarned}
            </div>
            <div className="text-gray-400 text-sm">
              ≈ ${(parseFloat(portfolioStats.totalEarned) * (usdPrice || 195.84)).toFixed(2)} USD
            </div>
          </div>

          {/* Pieces Created */}
          <div 
            className="p-6 rounded-2xl border border-[#2a2a2a] shadow-xl hover:shadow-2xl transition-all duration-200 hover:scale-105"
            style={{ backgroundColor: '#111111' }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-gray-400 text-sm font-medium">Pieces Created</h3>
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Package size={20} className="text-white" />
              </div>
            </div>
            <div className={`text-2xl font-bold text-white mb-2 ${isLoadingStats ? 'animate-pulse' : ''}`}>
              {portfolioStats.piecesCreated}
            </div>
            <div className="text-gray-400 text-sm">Individual pieces</div>
          </div>

          {/* Capsules Created */}
          <div 
            className="p-6 rounded-2xl border border-[#2a2a2a] shadow-xl hover:shadow-2xl transition-all duration-200 hover:scale-105"
            style={{ backgroundColor: '#111111' }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-gray-400 text-sm font-medium">Capsules Created</h3>
              <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
                <Users size={20} className="text-blue-400" />
              </div>
            </div>
            <div className={`text-2xl font-bold text-white mb-2 ${isLoadingStats ? 'animate-pulse' : ''}`}>
              {portfolioStats.capsulesCreated}
            </div>
            <div className="text-gray-400 text-sm">Collections</div>
          </div>

          {/* Outfits Created */}
          <div 
            className="p-6 rounded-2xl border border-[#2a2a2a] shadow-xl hover:shadow-2xl transition-all duration-200 hover:scale-105"
            style={{ backgroundColor: '#111111' }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-gray-400 text-sm font-medium">Outfits Created</h3>
              <div className="w-10 h-10 bg-pink-500/20 rounded-xl flex items-center justify-center">
                <Star size={20} className="text-pink-400" />
              </div>
            </div>
            <div className={`text-2xl font-bold text-white mb-2 ${isLoadingStats ? 'animate-pulse' : ''}`}>
              {portfolioStats.outfitsCreated}
            </div>
            <div className="text-gray-400 text-sm">Complete outfits</div>
          </div>

          {/* Pieces Owned */}
          <div 
            className="p-6 rounded-2xl border border-[#2a2a2a] shadow-xl hover:shadow-2xl transition-all duration-200 hover:scale-105"
            style={{ backgroundColor: '#111111' }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-gray-400 text-sm font-medium">Pieces Owned</h3>
              <div className="w-10 h-10 bg-indigo-500/20 rounded-xl flex items-center justify-center">
                <BarChart3 size={20} className="text-indigo-400" />
              </div>
            </div>
            <div className={`text-2xl font-bold text-white mb-2 ${isLoadingStats ? 'animate-pulse' : ''}`}>
              {portfolioStats.piecesOwned}
            </div>
            <div className="text-gray-400 text-sm">In collection</div>
          </div>

            {/* Active Listings */}
            <div 
              className="p-6 rounded-2xl border border-[#2a2a2a] shadow-xl hover:shadow-2xl transition-all duration-200 hover:scale-105"
              style={{ backgroundColor: '#111111' }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-gray-400 text-sm font-medium">Active Listings</h3>
                <div className="w-10 h-10 bg-orange-500/20 rounded-xl flex items-center justify-center">
                  <Eye size={20} className="text-orange-400" />
                </div>
              </div>
              <div className={`text-2xl font-bold text-white mb-2 ${isLoadingStats ? 'animate-pulse' : ''}`}>
                {portfolioStats.activeListings}
              </div>
              <div className="text-gray-400 text-sm">Items for sale</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8">
          {/* Recent Activity */}
          <div 
            className="rounded-2xl border border-[#2a2a2a] shadow-xl overflow-hidden"
            style={{ backgroundColor: '#111111' }}
          >
            <div className="bg-[#111111] px-6 py-4 border-b border-[#2a2a2a]">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white">Your Activity Feed</h3>
                  {publicKey && (
                    <p className="text-gray-400 text-sm">
                      {recentActivity.length} events 
                      {selectedTimeframe === '24h' ? ' (last 24 hours)' :
                       selectedTimeframe === '7d' ? ' (last 7 days)' :
                       selectedTimeframe === '30d' ? ' (last 30 days)' :
                       selectedTimeframe === '90d' ? ' (last 90 days)' : ' (last year)'} 
                      • Wallet: {publicKey.toString().slice(0, 8)}...{publicKey.toString().slice(-6)}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-blue-400 font-medium">Your Wallet Only</span>
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
              {!isConnected ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Activity size={32} className="text-gray-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-400 mb-2">Connect Your Wallet</h3>
                  <p className="text-gray-500 text-sm">Connect your wallet to view your activity and transactions</p>
                </div>
              ) : recentActivity.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Activity size={32} className="text-gray-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-400 mb-2">No Recent Activity</h3>
                  <p className="text-gray-500 text-sm">Your wallet activity will appear here once you start creating and trading pieces</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#2a2a2a]">
                      <th className="text-left p-4 font-semibold text-white">EVENT</th>
                      <th className="text-left p-4 font-semibold text-white">PIECE</th>
                      <th className="text-left p-4 font-semibold text-white">PRICE</th>
                      <th className="text-left p-4 font-semibold text-white">QUANTITY</th>
                      <th className="text-left p-4 font-semibold text-white">FROM</th>
                      <th className="text-left p-4 font-semibold text-white">TO</th>
                      <th className="text-left p-4 font-semibold text-white">TIME</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentActivity.map((activity) => {
                      const eventType = eventTypes[activity.type] || { icon: Activity, label: activity.eventLabel };
                      const EventIcon = eventType.icon;
                      const isYourFrom = activity.from && publicKey && activity.from === publicKey.toString();
                      const isYourTo = activity.to && publicKey && activity.to === publicKey.toString();
                      
                      return (
                        <tr 
                          key={activity.id} 
                          className="border-b border-[#2a2a2a] border-l-4 border-l-transparent transition-all duration-200 hover:bg-[#0a0a0a] hover:border-l-white/50"
                          style={{ contain: 'layout style paint' }}
                        >
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#0a0a0a] border border-[#2a2a2a]">
                                <EventIcon size={16} className="text-white" />
                              </div>
                              <span className="text-white font-medium">{eventType.label}</span>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              {activity.image && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openPieceDetailModal(activity);
                                  }}
                                  className="hover:opacity-80 transition-opacity"
                                  title={`View ${activity.pieceName}`}
                                >
                                  <img 
                                    src={activity.image} 
                                    alt={activity.pieceName}
                                    className="w-12 h-12 rounded-xl object-cover border border-[#2a2a2a] hover:border-white/50 transition-colors"
                                  />
                                </button>
                              )}
                              <div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openPieceDetailModal(activity);
                                  }}
                                  className="font-semibold text-white hover:text-gray-300 transition-colors text-left"
                                  title={`View ${activity.pieceName}`}
                                >
                                  {activity.pieceName}
                                </button>
                                <p className="text-gray-400 text-sm">{activity.collection}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4">
                            {activity.price !== '-' ? (
                              <div>
                                <span className="text-white font-semibold">{activity.price}</span>
                                {activity.usdPrice && (
                                  <p className="text-gray-400 text-sm">{activity.usdPrice}</p>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-500">-</span>
                            )}
                          </td>
                          <td className="p-4">
                            <span className="px-2 py-1 rounded-lg text-xs font-medium bg-white/20 text-white border border-white/30">
                              {activity.quantity}
                            </span>
                          </td>
                          <td className="p-4">
                            {activity.from !== '-' ? (
                              <div className="flex items-center gap-2">
                                <span className={`font-mono text-sm ${isYourFrom ? 'text-blue-400 font-semibold' : 'text-gray-400'}`}>
                                  {isYourFrom ? 'You' : `${activity.from.slice(0, 8)}...`}
                                </span>
                                {!isYourFrom && (
                                  <button
                                    onClick={() => copyToClipboard(activity.from, `from-${activity.id}`)}
                                    className="p-1 rounded hover:bg-[#0a0a0a] transition-colors"
                                  >
                                    {copiedAddress === `from-${activity.id}` ? (
                                      <CheckCircle size={12} className="text-green-400" />
                                    ) : (
                                      <Copy size={12} className="text-gray-400" />
                                    )}
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-500">-</span>
                            )}
                          </td>
                          <td className="p-4">
                            {activity.to !== '-' ? (
                              <div className="flex items-center gap-2">
                                <span className={`font-mono text-sm ${isYourTo ? 'text-blue-400 font-semibold' : 'text-gray-400'}`}>
                                  {isYourTo ? 'You' : `${activity.to.slice(0, 8)}...`}
                                </span>
                                {!isYourTo && (
                                  <button
                                    onClick={() => copyToClipboard(activity.to, `to-${activity.id}`)}
                                    className="p-1 rounded hover:bg-[#0a0a0a] transition-colors"
                                  >
                                    {copiedAddress === `to-${activity.id}` ? (
                                      <CheckCircle size={12} className="text-green-400" />
                                    ) : (
                                      <Copy size={12} className="text-gray-400" />
                                    )}
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-500">-</span>
                            )}
                          </td>
                          <td className="p-4">
                            <div className="text-gray-400">{activity.timeAgo}</div>
                            {activity.signature && (
                              <a 
                                href={`https://explorer.solana.com/tx/${activity.signature}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-400 hover:text-blue-300 hover:underline flex items-center gap-1 mt-1"
                              >
                                View TX
                              </a>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Piece Detail Modal */}
        {pieceDetailModal.isOpen && pieceDetailModal.item && (
          <div 
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={closePieceDetailModal}
          >
            <div 
              className="bg-[#111111] border border-[#2a2a2a] rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col relative"
              onClick={(e) => e.stopPropagation()}
              style={{ overflow: 'visible' }}
            >
              <div className="flex flex-1 overflow-hidden rounded-2xl" style={{ position: 'relative', zIndex: 1 }}>

              {/* Left side - 3D Model / Image */}
              <div className="w-1/2 p-6 overflow-y-auto">
                {/* 3D Model Toggle Buttons - Always show */}
                <div className="flex mb-4 bg-[#111111] rounded-xl p-1">
                  <button
                    onClick={() => {
                      console.log('🔘 Switching to 3D view');
                      setModalViewMode('3d');
                    }}
                    className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                      modalViewMode === '3d'
                        ? 'text-white bg-[#2a2a2a] shadow-lg' 
                        : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                    }`}
                  >
                    3D Model
                  </button>
                  <button
                    onClick={() => {
                      console.log('🔘 Switching to thumbnail view');
                      setModalViewMode('thumbnail');
                    }}
                    className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                      modalViewMode === 'thumbnail'
                        ? 'text-white bg-[#2a2a2a] shadow-lg' 
                        : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                    }`}
                  >
                    Thumbnail
                  </button>
                </div>

                {/* Model/Image Display */}
                <div className="rounded-xl flex items-center justify-center mb-1 overflow-hidden aspect-square bg-[#1a1a1a]">
                  {(() => {
                    const normalizedPiece = pieceDetailModal.item ? normalizePieces([pieceDetailModal.item])[0] : null;
                    
                    console.log('🖼️ [PORTFOLIO] Rendering view mode:', modalViewMode);
                    console.log('🖼️ [PORTFOLIO] Normalized piece for display:', {
                      previewUrl: normalizedPiece?.previewUrl,
                      thumbnailUrl: normalizedPiece?.thumbnailUrl,
                      has3DModel: normalizedPiece?.has3DModel
                    });
                    
                    if (modalViewMode === '3d') {
                      // Try to get bucket/key info from the piece data
                      const modelBucket = pieceDetailModal.item?.preview_bucket || pieceDetailModal.item?.model_bucket;
                      const modelKey = pieceDetailModal.item?.preview_key || pieceDetailModal.item?.model_key;
                      
                      // Fallback to legacy URL if bucket/key not available
                      const fallbackSrc = normalizedPiece?.previewUrl || normalizedPiece?.modelUrl;
                      
                      if (modelBucket && modelKey) {
                        console.log('🎮 [PORTFOLIO] Rendering ModelViewer with bucket/key:', { modelBucket, modelKey });
                        const customOrientation = pieceDetailModal.item?.customOrientation || null;
                        
                        return (
                          <ModelViewer
                            modelBucket={modelBucket}
                            modelKey={modelKey}
                            posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                            orientation={customOrientation}
                            className="w-full h-full modal"
                            onLoad={() => {
                              console.log('✅ [PORTFOLIO] Modal: 3D model loaded successfully');
                              setThreeDStatus('loaded');
                            }}
                            onError={(error) => {
                              console.error('❌ [PORTFOLIO] Modal: 3D model error:', error);
                              setThreeDStatus('error');
                              setThreeDError(error.message || 'Failed to load 3D model');
                            }}
                          />
                        );
                      } else if (fallbackSrc) {
                        console.log('🎮 [PORTFOLIO] Rendering ModelViewer with URL fallback:', fallbackSrc);
                        const customOrientation = pieceDetailModal.item?.customOrientation || null;
                        
                        return (
                          <ModelViewer
                            src={fallbackSrc}
                            posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                            orientation={customOrientation}
                            className="w-full h-full modal"
                            onLoad={() => {
                              console.log('✅ [PORTFOLIO] Modal: 3D model loaded successfully');
                              setThreeDStatus('loaded');
                            }}
                            onError={(error) => {
                              console.error('❌ [PORTFOLIO] Modal: 3D model error:', error);
                              setThreeDStatus('error');
                              setThreeDError(error.message || 'Failed to load 3D model');
                            }}
                          />
                        );
                      } else {
                        console.warn('⚠️ [PORTFOLIO] No 3D model source found:', { 
                          modelBucket, 
                          modelKey, 
                          fallbackSrc,
                          preview_glb_url: pieceDetailModal.item?.preview_glb_url,
                          model_url: pieceDetailModal.item?.model_url 
                        });
                        return (
                          <div className="w-full h-full flex items-center justify-center bg-[#0a0a0a] rounded-xl">
                            <div className="text-center text-white p-6">
                              <div className="text-6xl mb-4">⚠️</div>
                              <div className="text-xl font-semibold mb-2">No 3D model available</div>
                              <div className="text-sm text-gray-400">This piece doesn't have a 3D model</div>
                            </div>
                          </div>
                        );
                      }
                    } else {
                      // Show thumbnail image - use normalized piece data to ensure proper field names
                      return normalizedPiece?.thumbnailUrl ? (
                        <img 
                          src={normalizedPiece.thumbnailUrl} 
                          alt={normalizedPiece.name || pieceDetailModal.item.name}
                          className="w-full h-full object-cover rounded-xl"
                          onError={(e) => {
                            console.log('Modal thumbnail load error for:', normalizedPiece.name, 'URL:', normalizedPiece.thumbnailUrl);
                            e.target.src = '/images/piece-placeholder.png';
                            e.target.onerror = null;
                          }}
                        />
                      ) : pieceDetailModal.item?.thumbnail?.dataUrl ? (
                        <img 
                          src={pieceDetailModal.item.thumbnail.dataUrl} 
                          alt={pieceDetailModal.item.name}
                          className="w-full h-full object-cover rounded-xl"
                          onError={(e) => {
                            e.target.src = '/images/piece-placeholder.png';
                            e.target.onerror = null;
                          }}
                        />
                      ) : (
                        <img 
                          src="/images/piece-placeholder.png" 
                          alt="No preview available"
                          className="w-full h-full object-cover rounded-xl"
                        />
                      );
                    }
                  })()}
                </div>

                {/* Creator and Holder Section */}
                <div className="space-y-4 mb-6">
                  {/* Original Creator */}
                  <div>
                    <div className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2 px-1">
                      Original Creator
                    </div>
                    <button 
                      onClick={() => handleAccountClick(pieceDetailModal.item)}
                      className="w-full flex items-center gap-3 p-3 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-xl transition-colors group border border-[#1a1a1a]"
                    >
                      <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                        {(pieceDetailModal.item?.originalCreator?.profilePicture || pieceDetailModal.item?.creator?.profilePicture || pieceDetailModal.item?.originalCreator?.profile_picture_url || pieceDetailModal.item?.creator?.profile_picture_url) ? (
                          <img
                            src={pieceDetailModal.item?.originalCreator?.profilePicture || pieceDetailModal.item?.creator?.profilePicture || pieceDetailModal.item?.originalCreator?.profile_picture_url || pieceDetailModal.item?.creator?.profile_picture_url}
                            alt={pieceDetailModal.item?.originalCreator?.username || pieceDetailModal.item?.creator?.username || 'Creator'}
                            className="w-full h-full object-cover rounded-full"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-sm">
                              {(pieceDetailModal.item?.originalCreator?.username || pieceDetailModal.item?.creator?.username || 'Creator').charAt(0)}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="flex items-center gap-1.5">
                          <div className="text-white font-medium text-sm group-hover:text-gray-200 transition-colors truncate">
                            {pieceDetailModal.item?.originalCreator?.username || pieceDetailModal.item?.creator?.username || 'Original Creator'}
                          </div>
                          {(pieceDetailModal.item?.originalCreator?.verified || pieceDetailModal.item?.creator?.verified) && (
                            <VerifiedBadge 
                              size="sm"
                              verificationType={pieceDetailModal.item?.originalCreator?.verificationType || pieceDetailModal.item?.creator?.verificationType || 'none'}
                            />
                          )}
                        </div>
                        <div className="text-gray-400 text-xs mt-0.5">
                          {pieceDetailModal.item?.originalCreator?.piecesCount || pieceDetailModal.item?.creator?.piecesCount || '0'} pieces created
                        </div>
                      </div>
                      <div className="text-gray-400 group-hover:text-white transition-colors flex-shrink-0">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 18l6-6-6-6"/>
                        </svg>
                      </div>
                    </button>

                    {/* Creator Royalty - Outside button */}
                    {pieceDetailModal.item?.royalty_bps !== undefined && pieceDetailModal.item?.royalty_bps !== null && (
                      <div className="mt-3 px-1">
                        <span className="inline-block px-2 py-0.5 bg-green-500/10 border border-green-500/30 rounded-md text-green-400 text-xs">
                          {(pieceDetailModal.item.royalty_bps / 100).toFixed(1)}% Creator Royalty
                        </span>
                      </div>
                    )}

                    {/* Social Links */}
                    {(() => {
                      // Prioritize capsule social links if piece belongs to a capsule, otherwise use piece social links
                      const socialLinks = pieceCapsuleInfo 
                        ? {
                            website: pieceCapsuleInfo.website,
                            twitter: pieceCapsuleInfo.twitter,
                            instagram: pieceCapsuleInfo.instagram,
                            discord: pieceCapsuleInfo.discord
                          }
                        : (pieceDetailModal.item?.social_links || {});
                      const hasSocialLinks = socialLinks.website || socialLinks.twitter || socialLinks.instagram || socialLinks.discord;
                      
                      if (!hasSocialLinks) return null;
                      
                      return (
                        <div className="flex items-center gap-2 mt-3 px-1">
                          {socialLinks.website && (
                            <a
                              href={socialLinks.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                              title="Website"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white transition-colors">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="2" y1="12" x2="22" y2="12"/>
                                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                              </svg>
                            </a>
                          )}
                          {socialLinks.twitter && (
                            <a
                              href={socialLinks.twitter}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                              title="X (Twitter)"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400 hover:text-white transition-colors">
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                              </svg>
                            </a>
                          )}
                          {socialLinks.instagram && (
                            <a
                              href={socialLinks.instagram}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                              title="Instagram"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white transition-colors">
                                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                              </svg>
                            </a>
                          )}
                          {socialLinks.discord && (
                            <a
                              href={socialLinks.discord}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                              title="Discord"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400 hover:text-white transition-colors">
                                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                              </svg>
                            </a>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Right side - Details */}
              <div className="w-1/2 p-6 border-l border-[#2a2a2a] overflow-y-auto">
                <div className="flex justify-between items-start mb-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-2xl font-bold text-white">
                        {pieceDetailModal.item?.name}
                      </h2>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(pieceDetailModal.item.id);
                        }}
                        className="p-1.5 transition-all duration-300 hover:scale-110 active:scale-95"
                        title={isFavorited(pieceDetailModal.item.id) ? "Remove from favorites" : "Add to favorites"}
                      >
                        <Heart 
                          size={20} 
                          className={`transition-all duration-300 ${
                            isFavorited(pieceDetailModal.item.id)
                              ? 'text-red-500 fill-red-500'
                              : 'text-gray-400 hover:text-red-500'
                          }`} 
                        />
                      </button>
                    </div>
                    {/* Custom Tags */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {pieceDetailModal.item?.tags && pieceDetailModal.item.tags.length > 0 ? (
                        pieceDetailModal.item.tags.map((tag, index) => (
                          <span 
                            key={index}
                            className="bg-gray-600 text-gray-200 px-3 py-1 rounded-full text-sm font-medium"
                          >
                            {tag}
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-400 text-sm">No tags</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setActivityPanelOpen(!activityPanelOpen)}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                      activityPanelOpen 
                        ? 'bg-[#2a2a2a] text-white hover:bg-[#3a3a3a] border border-[#3a3a3a]' 
                        : 'bg-[#1a1a1a] text-gray-300 hover:bg-[#2a2a2a] hover:text-white border border-[#2a2a2a]'
                    }`}
                  >
                    Activity
                  </button>
                </div>

                <div className="space-y-5">
                  {/* Edition Selector - Only show if multiple editions */}
                  {(() => {
                    const hasMultipleEditions = allEditions.length > 1;
                    const currentPiece = allEditions.length > 0 ? allEditions[selectedEditionIndex] : pieceDetailModal.item;
                    
                    return hasMultipleEditions && (
                      <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                        <div className="mb-2">
                          <span className="text-gray-400 text-sm font-semibold">Select Piece</span>
                        </div>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowEditionSelector(!showEditionSelector);
                          }}
                          className="w-full flex items-center justify-between p-3 bg-[#111111] hover:bg-[#1a1a1a] rounded-lg transition-all border border-[#2a2a2a]"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-white text-sm font-medium">Piece #{selectedEditionIndex + 1}</span>
                            <span className="text-xs text-gray-500 font-mono">
                              {currentPiece.mint_address?.slice(0, 8)}...
                            </span>
                          </div>
                          {showEditionSelector ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                        </button>

                        {showEditionSelector && (
                          <div className="mt-2 max-h-40 overflow-y-auto bg-[#111111] rounded-lg border border-[#2a2a2a]">
                            {allEditions.map((piece, index) => (
                              <button
                                key={piece.id || index}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedEditionIndex(index);
                                  setShowEditionSelector(false);
                                }}
                                className={`w-full flex items-center justify-between p-2.5 hover:bg-[#1a1a1a] transition-all text-left ${
                                  index === selectedEditionIndex ? 'bg-[#1a1a1a]' : ''
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-white text-sm">Piece #{index + 1}</span>
                                  <span className="text-xs text-gray-500 font-mono">
                                    {piece.mint_address?.slice(0, 6)}...{piece.mint_address?.slice(-4)}
                                  </span>
                                </div>
                                {index === selectedEditionIndex && (
                                  <span className="text-green-400 text-xs font-medium">✓</span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Category and Color */}
                  {(() => {
                    const currentPiece = allEditions.length > 0 ? allEditions[selectedEditionIndex] : pieceDetailModal.item;
                    return (
                      <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                            <span className="text-gray-300 text-sm font-medium">
                              {getCategoryDisplayName(currentPiece?.category) || 'Uncategorized'}
                            </span>
                          </div>
                          {currentPiece?.color && (
                            <div className="text-gray-300 text-sm">
                              <span className="text-gray-500">Color: </span>
                              <span className="capitalize font-medium">{currentPiece.color}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Description */}
                  {(() => {
                    const currentPiece = allEditions.length > 0 ? allEditions[selectedEditionIndex] : pieceDetailModal.item;
                    return (
                      <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                        <h3 className="text-white font-semibold mb-2 text-sm">Description</h3>
                        <p className="text-gray-400 text-sm leading-relaxed">
                          {currentPiece?.description || 'A premium digital fashion piece with modern styling and high-quality design.'}
                        </p>
                      </div>
                    );
                  })()}

                  {/* Price */}
                  {(() => {
                    const currentPiece = allEditions.length > 0 ? allEditions[selectedEditionIndex] : pieceDetailModal.item;
                    return (
                      <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                        <h3 className="text-white font-semibold mb-3 text-sm">Pricing</h3>
                        <div className="space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400 text-sm">Price</span>
                            <div className="text-green-400 font-bold text-xl">
                              {(() => {
                                const displayPrice = currentPiece?.listing_price ?? currentPiece?.price ?? 0;
                                const formatted = typeof displayPrice === 'number' 
                                  ? displayPrice.toFixed(4).replace(/\.?0+$/, '')
                                  : displayPrice;
                                return formatted;
                              })()} SOL
                            </div>
                          </div>
                          <div className="text-right text-gray-500 text-sm">
                            ≈ ${(() => {
                              const displayPrice = currentPiece?.listing_price ?? currentPiece?.price ?? 0;
                              return ((displayPrice || 0) * (solPrice || 150)).toFixed(2);
                            })()} USD
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Scarcity */}
                  {(() => {
                    const currentPiece = allEditions.length > 0 ? allEditions[selectedEditionIndex] : pieceDetailModal.item;
                    return (
                      <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                        <div className="text-gray-400 text-sm mb-1">Scarcity</div>
                        <div className="text-white font-semibold text-lg">
                          {allEditions.length > 1 ? allEditions.length : (currentPiece?.quantity || 1)}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Dropped Date */}
                  <div className="text-gray-500 text-sm pt-2">
                    Dropped {pieceDetailModal.item?.created_at ? new Date(pieceDetailModal.item.created_at).toLocaleDateString('en-US', { 
                      year: '2-digit', 
                      month: '2-digit', 
                      day: '2-digit' 
                    }) : pieceDetailModal.item?.createdAt ? new Date(pieceDetailModal.item.createdAt).toLocaleDateString('en-US', { 
                      year: '2-digit', 
                      month: '2-digit', 
                      day: '2-digit' 
                    }) : 'Recently'}
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3 pt-4 pb-2">
                    {/* PRIMARY & SECONDARY MARKETPLACE BUTTONS */}
                    {(() => {
                      const currentPiece = allEditions.length > 0 ? allEditions[selectedEditionIndex] : pieceDetailModal.item;
                      const isOwner = isConnected && publicKey && 
                        currentPiece?.holder_wallet === publicKey.toString();
                      
                      // Check if piece is owned by the original creator (primary sale)
                      const creatorWallet = currentPiece?.creator_wallet || 
                                           currentPiece?.creators?.wallet ||
                                           currentPiece?.originalCreator?.wallet;
                      const isOwnedByCreator = currentPiece?.holder_wallet === creatorWallet;
                      
                      const listEligibility = canListPiece(wallet, currentPiece);
                      const purchaseEligibility = canPurchaseListing(wallet, activeListing, currentPiece);
                      
                      return (
                        <>
                          {/* OWNERSHIP BADGE: Show when user owns the piece */}
                          {isOwner && (
                            <div className="w-full px-4 py-3 bg-green-600/20 text-green-400 rounded-xl text-center border border-green-600/30 mb-2">
                              <span className="text-sm font-medium">✓ You own this piece</span>
                            </div>
                          )}
                          
                          {/* PRIMARY SALE: Buy directly from creator (no listing needed) */}
                          {!isOwner && isOwnedByCreator && 
                           currentPiece?.mint_address && 
                           !activeListing &&
                           currentPiece?.listing_status !== 'sold' && (
                            <button
                              onClick={() => handleBuyNow(currentPiece)}
                              className="w-full px-4 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg hover:shadow-blue-500/25"
                            >
                              Buy Now - {(currentPiece?.listing_price || currentPiece?.price || 0).toFixed(4)} SOL
                            </button>
                          )}
                          
                          {/* SECONDARY SALE: Buyer can purchase listed piece */}
                          {!isOwner && activeListing && activeListing.status === 'active' && 
                           currentPiece?.listing_status === 'listed' && (
                            <button
                              onClick={() => handlePurchaseListing(currentPiece)}
                              disabled={listingLoading || !purchaseEligibility.canPurchase}
                              className="w-full px-4 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg hover:shadow-blue-500/25 disabled:opacity-50"
                              title={!purchaseEligibility.canPurchase ? purchaseEligibility.reason : ''}
                            >
                              {listingLoading ? 'Processing...' : `Buy Now - ${activeListing.price_sol} SOL`}
                            </button>
                          )}
                          
                          {/* FALLBACK: Show buy button for listed pieces even if activeListing hasn't loaded yet */}
                          {!isOwner && !isOwnedByCreator && !activeListing &&
                           currentPiece?.listing_status === 'listed' &&
                           currentPiece?.listing_price && (
                            <button
                              onClick={() => handleBuyNow(currentPiece)}
                              disabled={listingLoading}
                              className="w-full px-4 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg hover:shadow-blue-500/25 disabled:opacity-50"
                            >
                              {listingLoading ? 'Loading...' : `Buy Now - ${(currentPiece?.listing_price || 0).toFixed(4)} SOL`}
                            </button>
                          )}
                          
                          {/* Show sold status only if viewer is NOT the current owner */}
                          {currentPiece?.listing_status === 'sold' && !isOwner && (
                            <div className="w-full px-4 py-3.5 bg-gray-800 text-gray-400 rounded-xl text-center font-semibold text-sm">
                              ✅ Sold
                            </div>
                          )}
                          
                          {/* Show unlisted status */}
                          {currentPiece?.listing_status === 'unlisted' && !isOwner && !isOwnedByCreator && (
                            <div className="w-full px-4 py-3.5 bg-gray-800 text-gray-400 rounded-xl text-center font-semibold text-sm">
                              Not for Sale
                            </div>
                          )}
                        </>
                      );
                    })()}
                    
                    {/* Make Offer and Request Trade Buttons */}
                    {(() => {
                      const currentPiece = allEditions.length > 0 ? allEditions[selectedEditionIndex] : pieceDetailModal.item;
                      const isOwner = isConnected && publicKey && 
                        currentPiece?.holder_wallet === publicKey.toString();
                      
                      // Don't show any action buttons if user owns the piece
                      if (isOwner) {
                        return null;
                      }
                      
                      return (
                        <div className="flex gap-3">
                          <button 
                            onClick={() => handleMakeOffer(currentPiece)}
                            className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 font-semibold text-sm border border-[#3a3a3a]"
                          >
                            Make Offer
                          </button>
                          <button 
                            onClick={() => handleRequestTrade(currentPiece)}
                            className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 font-semibold text-sm border border-[#3a3a3a] flex items-center justify-center gap-2"
                          >
                            <ArrowLeftRight size={16} />
                            Request Trade
                          </button>
                        </div>
                      );
                    })()}

                    {/* View Entire Capsule Button - Only show if piece belongs to a capsule */}
                    {pieceCapsuleInfo && (
                      <div className="pt-3">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewCapsule();
                          }}
                          className="w-full px-4 py-2.5 bg-[#1a1a1a] hover:bg-[#2a2a2a] border border-[#3a3a3a] hover:border-[#4a4a4a] text-gray-300 hover:text-white rounded-lg transition-all duration-200 font-medium text-sm flex items-center justify-center gap-2"
                        >
                          <Package size={14} />
                          View entire capsule
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Activity Side Panel - slides out from behind modal on desktop, overlay on mobile */}
            <div 
              className="absolute top-0 left-0 md:left-full h-full w-full md:w-80 bg-[#0a0a0a] border-l border-[#2a2a2a] md:rounded-r-2xl transition-all duration-300 ease-in-out shadow-2xl"
              style={{ 
                transform: activityPanelOpen ? 'translateX(0)' : (window.innerWidth < 768 ? 'translateX(-100%)' : 'translateX(-100%)'),
                zIndex: activityPanelOpen ? 50 : 0,
                opacity: activityPanelOpen ? 1 : 0,
                visibility: activityPanelOpen ? 'visible' : 'hidden',
                pointerEvents: activityPanelOpen ? 'auto' : 'none'
              }}
            >
              <div className="h-full flex flex-col p-5">
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#2a2a2a]">
                  <h3 className="text-base font-bold text-white">Activity</h3>
                  <button
                    onClick={() => setActivityPanelOpen(false)}
                    className="p-1.5 hover:bg-[#1a1a1a] rounded-lg transition-colors"
                    title="Close Activity Panel"
                  >
                    <ChevronRight size={18} className="text-gray-400" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <PieceActivityAndHolders
                    pieceId={pieceDetailModal.item?.id}
                    pieceName={pieceDetailModal.item?.name}
                    basePrice={parseFloat(pieceDetailModal.item?.price || pieceDetailModal.item?.listing_price) || 0}
                    solPrice={solPrice || 150}
                    totalQuantity={allEditions.length > 1 ? allEditions.length : (pieceDetailModal.item?.quantity || 1)}
                    groupedPieceIds={groupedPieceIds}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

        {/* Request Trade Modal */}
        {requestTradeModal.isOpen && requestTradeModal.item && (
          <RequestTradeModal
            isOpen={requestTradeModal.isOpen}
            onClose={() => setRequestTradeModal({ isOpen: false, item: null })}
            piece={requestTradeModal.item}
          />
        )}
      </div>
    </div>
    </WalletRequired>
  );
};

export default Portfolio; 